import { FILTERABLE_ENTITY_TYPES } from 'advanced-filters/data';
import { ALL_COMPARATORS, COMPARATORS_WITHOUT_VALUE, CUSTOM_TIME_COMPARATORS } from 'advanced-filters/data/comparators';
import type { FilterForm } from 'advanced-filters/store/filter';

import { hasSubCondition } from './hasSubCondition';

const validateDateFilter = (timeFilter: FilterForm['timeFilter'], comparator?: FilterForm['conditionComparator']) => {
  const filterType = timeFilter?.filterType;
  if (!filterType) {
    return {
      error: 'A valid Date Filter is required',
    };
  }
  if (CUSTOM_TIME_COMPARATORS.includes(filterType)) {
    if (comparator === ALL_COMPARATORS.before && filterType === ALL_COMPARATORS.custom) {
      if (!timeFilter?.fromTime) {
        return {
          error: 'A value is required for custom date filters',
        };
      }
    } else if (comparator === ALL_COMPARATORS.after && filterType === ALL_COMPARATORS.custom) {
      if (!timeFilter?.toTime) {
        return {
          error: 'A value is required for custom date filters',
        };
      }
    } else if (filterType === ALL_COMPARATORS.custom) {
      if (!timeFilter?.fromTime || !timeFilter?.toTime) {
        return {
          error: 'From Time and To Time are required for custom date filters',
        };
      }
    }
    if (
      filterType === ALL_COMPARATORS.last_n_hours
      || filterType === ALL_COMPARATORS.last_n_days
      || filterType === ALL_COMPARATORS.next_n_days) {
      if (!timeFilter?.offsetValue) {
        return {
          error: 'A value is required for "Last N Days | Hours" or "Next N Days" filters',
        };
      }
    }
  }
  return null;
};

const validateValue = (
  dataType: FilterForm['conditionKey']['dataType'],
  comparator: FilterForm['conditionComparator'],
  value: FilterForm['conditionValue'],
) => {
  if (!value) {
    return {
      error: 'Condition Value is required',
    };
  }
  // If Number Validate between's from to range
  if (dataType === 'number' && comparator === 'between') {
    const { from, to } = value.value;
    const fromValue = parseInt(from, 10);
    const toValue = parseInt(to, 10);
    if ((
      Number.isNaN(fromValue) || Number.isNaN(toValue)
    ) || fromValue > toValue) {
      return {
        error: 'Invalid Number Range',
      };
    }
  }
  if (dataType === 'date') {
    const dateValidation = validateDateFilter(value.value?.timeFilter, comparator);
    if (dateValidation?.error) {
      return dateValidation;
    }
  }
  return null;
};

export const validatePendingFilter = (
  filter: Partial<FilterForm>,
  existingFilters: FilterForm[],
): { error?: string; filter?: FilterForm } => {
  const {
    conditionKey,
    conditionComparator,
    conditionValue,
    subConditionKey,
    subConditionComparator,
    subConditionValue,
    timeFilter,
  } = filter;

  const doesFilterExist = existingFilters.some(
    (existingFilter) => existingFilter === filter,
  );

  if (doesFilterExist) {
    return {
      error: 'Filter already exists',
    };
  }

  // Data Validation
  if (!conditionKey || !conditionComparator) {
    return {
      error: 'Condition and Condition Operator are required',
    };
  }

  if (
    !COMPARATORS_WITHOUT_VALUE.includes(conditionComparator)
    && !conditionValue
  ) {
    return {
      error: 'Condition Value is required',
    };
  }

  // Validate Condition Value
  if (conditionValue) {
    const valueValidation = validateValue(conditionKey.dataType, conditionComparator, conditionValue);
    if (valueValidation?.error) {
      return valueValidation;
    }
  }

  // Validate Sub Condition
  if (hasSubCondition(conditionKey, conditionValue)) {
    // Activity Filter can be added without sub condition
    // e.g Lead Activity is Sales Activity
    if (conditionKey.entityType === FILTERABLE_ENTITY_TYPES.leadActivity && subConditionKey) {
      if (!subConditionComparator) {
        return {
          error: 'Sub Condition and Sub Condition Comparator are required',
        };
      }
      if (
        !COMPARATORS_WITHOUT_VALUE.includes(subConditionComparator)
        && !subConditionValue
      ) {
        return {
          error: 'Sub Condition Value is required',
        };
      }

      if (subConditionValue) {
        const valueValidation = validateValue(
          subConditionKey.dataType,
          subConditionComparator,
          subConditionValue,
        );
        if (valueValidation?.error) return valueValidation;
      }
    }
  }

  // Date filter validation
  if (conditionKey.hasDateFilter && conditionKey.entityType !== FILTERABLE_ENTITY_TYPES.leadActivity) {
    const dateValidation = validateDateFilter(timeFilter);
    if (dateValidation?.error) return dateValidation;
  }

  return {
    filter: filter as FilterForm,
  };
};
